import React from "react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import { CalendarRange } from "lucide-react";
import parseDuration from "../../utils/parseDuration";

// Helper: Prepare Area Chart (Total hours per day)
function getWeeklyLineData(activities, weekDays, lang) {
  const dailySum = weekDays.map(d => {
    const totalMin = activities
      .filter(a => a.date === d.key)
      .reduce((sum, a) => sum + (a.durationMin || 0), 0);
    return { x: d.label, y: +(totalMin / 60).toFixed(2) };
  });
  return [{
    id: lang === "th" ? "เวลาทั้งหมด" : "Total",
    color: "hsl(220,70%,50%)",
    data: dailySum,
  }];
}

// Helper: Prepare Stacked Bar Chart (hour per category per day)
function getWeeklyStackedBarData(activities, weekDays, categories, lang) {
  return weekDays.map(d => {
    const item = { day: d.label };
    categories.forEach(cat => {
      const totalMin = activities
        .filter(a => a.date === d.key && a.category === cat.value)
        .reduce((sum, a) => sum + (a.durationMin || 0), 0);
      item[cat.label[lang]] = +(totalMin / 60).toFixed(2);
    });
    return item;
  });
}

export default function WeekCard({ categories = [], activities = [], lang = "th", weekLabel, week }) {
//  const t = locales[lang];
  // weekStart ต้องเป็นจันทร์ของสัปดาห์ที่ filter อยู่จริง!
  const weekStart = week ? new Date(week) : new Date();
  if (!week) weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7)); // ไปจันทร์

  // 1. สร้าง array 7 วันในสัปดาห์
  const weekDays = [...Array(7)].map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const label = d.toLocaleDateString(lang === "th" ? "th-TH" : "en-US", { weekday: "short" });
    return { key, label };
  });

  // 2. Normalize activities (ปลอดภัยกับ timezone)
  const normalized = activities.map(a => {
    const d = new Date(a.activity_date || a.created_at);
    const localDate = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2,'0')}`;
    return {
      ...a,
      date: localDate,
      durationMin: typeof a.duration === "number"
        ? a.duration
        : parseDuration(a.duration) || 0
    };
  });

  // 3. Chart data
  const lineData = getWeeklyLineData(normalized, weekDays, lang);
  const barData = getWeeklyStackedBarData(normalized, weekDays, categories, lang);
  const barKeys = categories.map(cat => cat.label[lang]);

  return (
    <div className="bg-white rounded-2xl shadow-xl px-6 py-6 mb-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <CalendarRange className="text-indigo-500" size={22} />
        <span className="font-bold text-lg text-neutral-800">
          {weekLabel || (lang === "th" ? "สัปดาห์นี้" : "This Week")}
        </span>
      </div>
      {/* Area Chart: Total hours per day */}
      <div className="w-full h-48 mb-5">
        <div className="font-bold text-gray-700 mb-2 text-sm">
          {lang === "th" ? "เวลารวมต่อวัน" : "Total Hours per Day"}
        </div>
        <ResponsiveLine
          data={lineData}
          margin={{ top: 10, right: 30, bottom: 30, left: 40 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto" }}
          axisBottom={{ legend: lang === "th" ? "วัน" : "Day", legendOffset: 24 }}
          axisLeft={{ legend: lang === "th" ? "ชั่วโมง" : "Hours", legendOffset: -32 }}
          pointSize={8}
          colors={{ scheme: "set1" }}
          enableArea={true}
        />
      </div>

      {/* Stacked Bar: ชั่วโมงแต่ละหมวด */}
      <div className="w-full h-48">
        <div className="font-bold text-gray-700 mb-2 text-sm">
          {lang === "th" ? "เวลาต่อหมวดในแต่ละวัน" : "Time per Category per Day"}
        </div>
        <ResponsiveBar
          data={barData}
          keys={barKeys}
          indexBy="day"
          margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
          padding={0.3}
          groupMode="stacked"
          colors={{ scheme: "pastel1" }}
          axisBottom={{
            legend: lang === "th" ? "วัน" : "Day",
            legendPosition: "middle",
            legendOffset: 28,
          }}
          axisLeft={{
            legend: lang === "th" ? "ชั่วโมง" : "Hours",
            legendPosition: "middle",
            legendOffset: -36,
          }}
          enableGridY={true}
          labelSkipWidth={16}
          labelSkipHeight={12}
          labelTextColor="#222"
          animate={true}
        />
      </div>
    </div>
  );
}